// =============================================================
// Relative-overdue — pure logic.
//
// Ported from the prototype (scripts/perpetuum-device-card.jsx,
// perpetuum-desktop-sections.jsx): overdue is shown RELATIVE to each
// machine's interval, so a 3-day-late weekly job shouts louder than a
// 3-day-late yearly one. The bar fill is `late ÷ interval`, capped at 100%.
//
// Binding domain rule (PROJECT.md): "relative-overdue (late ÷ interval)".
// =============================================================

/** Severity band for an overdue signal — maps to the status palette. */
export type OverdueBand = "ok" | "warn" | "alarm";

export interface OverdueResult {
  /** Raw `late / interval` ratio (0 when on time; can exceed 1 when very late). */
  ratio: number;
  /** Ratio clamped to [0, 1] then ×100, rounded — the bar fill percentage. */
  percent: number;
  /** Severity band derived from the ratio. */
  band: OverdueBand;
}

/**
 * Default band thresholds on the relative ratio.
 * - on time (late <= 0)          → ok
 * - 0 < ratio <  warnAt          → warn
 * - ratio >= alarmAt             → alarm
 * Defaults: warn from any overdue, alarm at half-an-interval late.
 */
export interface OverdueThresholds {
  /** Ratio at/above which the band is "alarm". Default 0.5. */
  alarmAt: number;
}

export const DEFAULT_OVERDUE_THRESHOLDS: OverdueThresholds = {
  alarmAt: 0.5,
};

/**
 * Compute the relative-overdue signal.
 *
 * @param late     units late (days for calendar PM, hours for counter PM); <=0 means on time.
 * @param interval the PM interval in the SAME units; must be > 0.
 */
export function relativeOverdue(
  late: number,
  interval: number,
  thresholds: OverdueThresholds = DEFAULT_OVERDUE_THRESHOLDS,
): OverdueResult {
  if (!(interval > 0)) {
    throw new Error("relativeOverdue: interval must be > 0");
  }
  if (late <= 0) {
    return { ratio: 0, percent: 0, band: "ok" };
  }
  const ratio = late / interval;
  const percent = Math.min(100, Math.round(ratio * 100));
  const band: OverdueBand = ratio >= thresholds.alarmAt ? "alarm" : "warn";
  return { ratio, percent, band };
}
